import { type FC, type ReactNode, useState } from 'react'
import { Button } from '../../../components/Button/Button'
import { AccountOpeningCollections, type AccountOpeningCollectionStep } from '../../../models/AccountOpeningDescriptor'
import { CollectionItem } from './CollectionItem'
import { ExistingPartyRow } from './ExistingPartyRow'
import { PanelSchemaForm } from './PanelSchemaForm'
import {
  existingPartyPanel,
  isItemConfirmed,
  itemNeedsCustodianDetails,
  itemSkipsReview,
  formIdPrefix,
  sharedEdits,
  stepItems,
  type CollectionContext,
  type ItemGroup,
  type ResolvedItem,
} from '../domain/collections'
import { HintText, PrefillButton, StepConfirmBar, StepFooter } from './flowPrimitives'
import { LoadingScreen } from './LoadingScreen'
import { collectionCopy, confirmCopy, missingFieldsText } from '../descriptor/stepCopy'
import { useStepPrefill } from '../hooks/useStepPrefill'
import { useWizardFlow } from '../hooks/WizardFlowContext'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { asOfText } from '../domain/asOf'
import { type ItemPatch } from '../domain/types'
import { type PrefillResults } from '../domain/prefillSources'

interface CollectionStepProps {
  step: AccountOpeningCollectionStep
  collectionCtx: CollectionContext
  isSaving: boolean
  isFetching: boolean
  gated: boolean
  missingGroups: ItemGroup[]
  confirmedRefs: string[]
  canDiscard: boolean
  onItemChange: (item: ResolvedItem, patch: ItemPatch) => void
  onValuesFilled: (filled: PrefillResults) => void
  onConfirmItem: (item: ResolvedItem) => void
  onConfirmAll: () => void
  onDiscardChanges: () => void
}

// The custodian's details are shown as held, so the locked form never writes back to the draft.
const keepValues = (): void => undefined

export const CollectionStep: FC<CollectionStepProps> = ({
  step,
  collectionCtx,
  isSaving,
  isFetching,
  gated,
  missingGroups,
  confirmedRefs,
  canDiscard,
  onItemChange,
  onValuesFilled,
  onConfirmItem,
  onConfirmAll,
  onDiscardChanges,
}) => {
  const { readOnly } = useWizardFlow()
  const accountTimeZone = useAccountTimezone()
  const [expandedRefs, setExpandedRefs] = useState<string[]>([])
  const copy = collectionCopy(step)
  const confirm = confirmCopy(step)

  const shownItems = stepItems(step, collectionCtx)
  const isBlockScope = step.confirm?.scope === 'block'
  const existingPanel = existingPartyPanel(step)

  const fill = useStepPrefill({ step, items: shownItems, onFilled: onValuesFilled })

  if (gated) {
    return <HintText>{copy.gatedHint}</HintText>
  }

  if (isFetching) {
    return <LoadingScreen message={copy.loading} compact={step.itemLayout === 'row'} />
  }

  if (shownItems.length === 0) {
    return <HintText>{copy.empty}</HintText>
  }

  const missingByRef = new Map(missingGroups.map((group) => [group.key, group.items]))
  const allComplete = shownItems.every((item) => (missingByRef.get(item.ref) ?? []).length === 0 && !itemNeedsCustodianDetails(item))
  const allConfirmed = shownItems.every((item) => isItemConfirmed(item, confirmedRefs))

  const handleItemChange = (item: ResolvedItem, patch: ItemPatch) => {
    onItemChange(item, patch)
    sharedEdits(step, item, patch, collectionCtx).forEach((edit) => onItemChange(edit.item, edit.patch))
  }

  const toggleExpanded = (ref: string) => {
    setExpandedRefs((previous) => (previous.includes(ref) ? previous.filter((candidate) => candidate !== ref) : [...previous, ref]))
  }

  const handleConfirmItem = (item: ResolvedItem) => {
    onConfirmItem(item)
    setExpandedRefs((previous) => previous.filter((ref) => ref !== item.ref))
  }

  const blockPrefill = isBlockScope && fill.canFillAll ? <PrefillButton label={fill.label} loading={fill.isFillingAll} onClick={fill.runAll} /> : null

  const blockConfirm =
    isBlockScope && !readOnly && !allConfirmed ? (
      <StepConfirmBar
        message={allComplete ? confirm.readyMessage : confirm.blockedMessage}
        label={confirm.label}
        disabled={isSaving || !allComplete}
        loading={isSaving}
        discard={
          confirm.discardLabel && canDiscard ? (
            <Button variant='secondary' disabled={isSaving} onClick={onDiscardChanges}>
              {confirm.discardLabel}
            </Button>
          ) : null
        }
        prefill={blockPrefill}
        onConfirm={onConfirmAll}
      />
    ) : null

  const itemFooter = (item: ResolvedItem): ReactNode => {
    if (isBlockScope) {
      return null
    }
    const missing = missingByRef.get(item.ref) ?? []
    const complete = missing.length === 0
    const confirmed = isItemConfirmed(item, confirmedRefs)
    const prefill = fill.canFill(item) ? <PrefillButton label={fill.label} loading={fill.isFilling(item)} onClick={() => fill.runFor(item)} /> : null

    const itemConfirm =
      step.confirm?.scope === 'item' && !readOnly && !confirmed ? (
        <StepConfirmBar
          message={
            complete
              ? confirm.readyMessage
              : missingFieldsText(
                  confirm.itemBlockedMessage,
                  missing.map((entry) => entry.label),
                )
          }
          label={confirm.label}
          disabled={!complete}
          prefill={prefill}
          onConfirm={() => handleConfirmItem(item)}
        />
      ) : null

    return <StepFooter confirm={itemConfirm} prefill={prefill} error={fill.errorFor(item)} />
  }

  return (
    <div className={step.itemLayout === 'card' ? 'd-flex flex-column zocks-gap-1.5' : 'd-flex flex-column zocks-gap-1'}>
      {shownItems.map((item) =>
        itemSkipsReview(item) ? (
          <ExistingPartyRow
            key={item.ref}
            heading={item.heading}
            subheading={item.subheading}
            source={asOfText(
              collectionCtx.copy.existingPartySource,
              item.collection === AccountOpeningCollections.PARTIES ? item.party.custodianDetailsAt : undefined,
              accountTimeZone,
            )}
            noReview={collectionCtx.copy.existingPartyNoReview}
            expanded={expandedRefs.includes(item.ref)}
            onToggleExpand={existingPanel && !itemNeedsCustodianDetails(item) ? () => toggleExpanded(item.ref) : undefined}
          >
            {existingPanel ? (
              <PanelSchemaForm item={item} panel={existingPanel} missing={[]} readOnly idPrefix={formIdPrefix(step.id, item)} onValuesChange={keepValues} />
            ) : null}
          </ExistingPartyRow>
        ) : (
          <CollectionItem
            key={item.ref}
            step={step}
            item={item}
            missing={missingByRef.get(item.ref) ?? []}
            confirmed={isItemConfirmed(item, confirmedRefs)}
            expanded={expandedRefs.includes(item.ref)}
            footer={itemFooter(item)}
            onToggleExpand={() => toggleExpanded(item.ref)}
            onChange={(patch) => handleItemChange(item, patch)}
          />
        ),
      )}

      <StepFooter confirm={blockConfirm} prefill={blockPrefill} error={isBlockScope ? fill.error : null} />
    </div>
  )
}
